import { supabase } from './supabase';

const ADMIN_SESSION_KEY = 'lunins_cassems_admin_session_v1';

export type CassemsAdminSession = {
  token: string;
  expiresAt: number;
};

export type CassemsAdminMetrics = {
  range: { from: string; to: string };
  summary: {
    visitantesLanding: number;
    sessoesAddToCart: number;
    sessoesCheckout: number;
    pedidosTrackeados: number;
    pedidosRegistrados: number;
    receitaTotal: number;
    ticketMedio: number;
    itensVendidos: number;
    taxaConversao: number;
  };
  statusNotificacao: Array<{ status: string; total: number }>;
  topProdutos: Array<{ produto: string; quantidade: number; receita: number }>;
  origens: Array<{ origem: string; visitas: number; pedidos: number }>;
  dailySeries: Array<{
    dia: string;
    pageviews: number;
    visitantes: number;
    addToCart: number;
    checkout: number;
    pedidosTrackeados: number;
    pedidosRegistrados: number;
    receita: number;
  }>;
  recentOrders: Array<{
    id: string;
    createdAt: string;
    cliente: string;
    telefone: string;
    setor: string;
    total: number;
    itens: Array<{ nome: string; quantidade: number; subtotal: number }>;
    itensResumo: string;
    notificacaoStatus: string;
    notificacaoErro: string;
  }>;
};

export function getAdminSession(): CassemsAdminSession | null {
  if (typeof window === 'undefined') return null;
  const raw = window.sessionStorage.getItem(ADMIN_SESSION_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as CassemsAdminSession;
    if (!parsed.token || !parsed.expiresAt || Date.now() >= parsed.expiresAt) {
      window.sessionStorage.removeItem(ADMIN_SESSION_KEY);
      return null;
    }
    return parsed;
  } catch {
    window.sessionStorage.removeItem(ADMIN_SESSION_KEY);
    return null;
  }
}

export function saveAdminSession(session: CassemsAdminSession): void {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify(session));
}

export function clearAdminSession(): void {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(ADMIN_SESSION_KEY);
}

export async function loginCassemsAdmin(pin: string): Promise<CassemsAdminSession> {
  const { data, error } = await supabase.functions.invoke('cassems-admin-login', {
    body: { pin },
  });
  if (error) throw new Error(error.message || 'Falha ao validar PIN.');

  const payload = data as { ok?: boolean; token?: string; expiresAt?: number; error?: string };
  if (!payload?.ok || !payload.token || !payload.expiresAt) {
    throw new Error(payload?.error || 'PIN inválido.');
  }

  const session = { token: payload.token, expiresAt: payload.expiresAt };
  saveAdminSession(session);
  return session;
}

export async function fetchCassemsAdminMetrics(params: {
  token: string;
  from: string;
  to: string;
}): Promise<CassemsAdminMetrics> {
  const { data, error } = await supabase.functions.invoke('cassems-admin-metrics', {
    body: params,
  });
  if (error) throw new Error(error.message || 'Falha ao carregar métricas.');

  const payload = data as { ok?: boolean; error?: string } & CassemsAdminMetrics;
  if (!payload?.ok) throw new Error(payload?.error || 'Painel indisponível.');
  return payload;
}
